import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { ActivityScope } from '../lib/activity'
import { formatDistanceToNow, format } from 'date-fns'
import {
  Calendar, CheckCircle2, XCircle, Ban, Trash2, Gift, Coins,
  DollarSign, UserPlus, Activity as ActivityIcon
} from 'lucide-react'

type ActivityRow = {
  id: string
  scope: string
  action: string
  summary: string
  actor_email: string | null
  actor_role: string | null
  entity_table: string | null
  entity_id: string | null
  created_at: string
}

// Map an action to an icon + accent colour so the timeline reads at a glance.
function actionVisual(action: string): { Icon: typeof Calendar; tone: string } {
  if (action.includes('approved') || action.includes('accepted')) return { Icon: CheckCircle2, tone: 'text-emerald-500' }
  if (action.includes('rejected') || action.includes('declined')) return { Icon: XCircle, tone: 'text-rose-500' }
  if (action.includes('cancelled')) return { Icon: Ban, tone: 'text-gray-400' }
  if (action.includes('deleted') || action.includes('revoked')) return { Icon: Trash2, tone: 'text-rose-400' }
  if (action.includes('earned')) return { Icon: Gift, tone: 'text-amber-500' }
  if (action.includes('claimed') || action.includes('consulting')) return { Icon: Coins, tone: 'text-amber-400' }
  if (action.includes('topup')) return { Icon: DollarSign, tone: 'text-emerald-400' }
  if (action.includes('rate')) return { Icon: Coins, tone: 'text-brand-400' }
  if (action.includes('logged') || action.includes('session')) return { Icon: Calendar, tone: 'text-brand-400' }
  if (action.includes('student') || action.includes('created')) return { Icon: UserPlus, tone: 'text-brand-400' }
  return { Icon: ActivityIcon, tone: 'text-gray-400' }
}

const INITIAL = 10

/**
 * Reusable recent-activity timeline. Drives both the per-page clock-icon feed
 * (pass `scope`) and the per-student feed on the detail page (pass `studentId`).
 * Shows the latest `INITIAL` events with a "See more" to reveal the full history.
 */
export function ActivityFeed({
  scope,
  studentId,
  emptyHint = 'No activity yet.',
}: {
  scope?: ActivityScope
  studentId?: string
  emptyHint?: string
}) {
  const [rows, setRows] = useState<ActivityRow[]>([])
  const [loading, setLoading] = useState(true)
  const [showAll, setShowAll] = useState(false)

  const fetchRows = useCallback(async () => {
    setLoading(true)
    try {
      let query = supabase
        .from('activity_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(showAll ? 200 : INITIAL + 1)

      if (studentId) {
        // Per-student: student-scoped events plus session events tagged with this student.
        query = query.or(`entity_id.eq.${studentId},metadata->>studentId.eq.${studentId}`)
      } else if (scope) {
        query = query.eq('scope', scope)
      }

      const { data, error } = await query
      if (error) throw error
      setRows((data as ActivityRow[]) || [])
    } catch (e) {
      console.error('Error loading activity:', e)
    } finally {
      setLoading(false)
    }
  }, [scope, studentId, showAll])

  useEffect(() => { fetchRows() }, [fetchRows])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-400" />
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="py-8 text-center">
        <ActivityIcon className="h-6 w-6 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
        <p className="text-sm text-gray-500 dark:text-gray-400">{emptyHint}</p>
      </div>
    )
  }

  const visible = showAll ? rows : rows.slice(0, INITIAL)
  const hasMore = !showAll && rows.length > INITIAL

  return (
    <div>
      <ol className="relative">
        {visible.map((row, i) => {
          const { Icon, tone } = actionVisual(row.action)
          const isLast = i === visible.length - 1
          return (
            <li key={row.id} className="relative flex gap-3 pb-4">
              {/* timeline rail */}
              {!isLast && (
                <span className="absolute left-[15px] top-7 bottom-0 w-px bg-gray-200 dark:bg-gray-700" aria-hidden />
              )}
              <span className="relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                <Icon className={`h-4 w-4 ${tone}`} />
              </span>
              <div className="min-w-0 flex-1 pt-0.5">
                <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug">{row.summary}</p>
                <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500" title={format(new Date(row.created_at), 'PPpp')}>
                  {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
                  {row.actor_email ? ` · ${row.actor_email}` : ''}
                </p>
              </div>
            </li>
          )
        })}
      </ol>
      {hasMore && (
        <button
          onClick={() => setShowAll(true)}
          className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-medium text-brand-600 dark:text-brand-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          See more ({rows.length - INITIAL > 0 ? `${rows.length - INITIAL}+` : 'all'})
        </button>
      )}
      {showAll && rows.length > INITIAL && (
        <button
          onClick={() => setShowAll(false)}
          className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Show less
        </button>
      )}
    </div>
  )
}
